import React, { useState, useReducer, useEffect } from "react";
import { useAuditApi } from "../../../../services/AuditApi";
import AuditLogTable from "../../../auditLogs/AuditLogTable";
import ErrorMessage from "../../../messages/ErrorMessage";
import UtcDateRange from "../../../common/UtcDateRange";
import SearchButton from "../../../common/SearchButton";
import {
  getEmptyPageMetadata,
  getPreviousPageTokenForPageRead,
  pagingReducer,
} from "../../../../utils/pageMetadataHelper";

import "./UserAuditLogs.css";

const buildInitialDateRange = () => {
  const now = new Date();
  const aMonthAgo = new Date();
  aMonthAgo.setMonth(aMonthAgo.getMonth() - 1);
  return {
    dateFrom: new Date(
      aMonthAgo.getFullYear(),
      aMonthAgo.getMonth(),
      aMonthAgo.getDate()
    ).toISOString(),
    dateTo: new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    ).toISOString(),
  };
};

const pagingDefaults = {
  pageSize: 15,
};

const UserAuditLogs = ({ user }) => {
  const { fetchUserAuditLogs } = useAuditApi();
  const [errorMsg, setErrorMsg] = useState(null);
  const [showError, setShowError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cachedAuditLogPageSize, setCachedAuditLogPageSize] = useState(
    pagingDefaults.pageSize
  );
  const buildEmptyResultSet = () => {
    return {
      auditLogs: [],
      pageSize: cachedAuditLogPageSize,
    };
  };
  const [pageMetadata, dispatchPageMetadata] = useReducer(
    pagingReducer,
    getEmptyPageMetadata()
  );
  const [auditLogResultSet, setAuditLogResultSet] = useState(
    buildEmptyResultSet()
  );
  const initialDateRange = buildInitialDateRange();
  const [fromDateUtc, setFromDateUtc] = useState(initialDateRange.dateFrom);
  const [toDateUtc, setToDateUtc] = useState(initialDateRange.dateTo);

  const clearErrorMsg = () => {
    setErrorMsg(null);
    setShowError(false);
  };

  const showErrorMsg = (msg) => {
    setErrorMsg(msg);
    setShowError(true);
  };

  const isDateRangeValid = (fromDate, toDate) => {
    return fromDate && toDate && fromDate < toDate;
  };

  const getAuditLogs = async (
    _fromDateUtc = fromDateUtc,
    _toDateUtc = toDateUtc,
    pageSize = cachedAuditLogPageSize,
    continuationToken = null
  ) => {
    try {
      setBusy(true);
      clearErrorMsg();

      const fromDate = new Date(_fromDateUtc);
      const toDate = new Date(_toDateUtc);

      const fetchAuditLogsViaApi = async () => {
        const apiResult = await fetchUserAuditLogs(
          user.userId,
          fromDate,
          toDate,
          pageSize,
          continuationToken
        );

        if (!apiResult?.success || !apiResult?.data)
          throw new Error(
            apiResult?.error?.description || "Unexpected response"
          );

        return {
          auditLogs: apiResult.data.auditLogs,
          pageSize,
          nextPageToken: apiResult.data.forwardToken,
        };
      };

      const resultSet = isDateRangeValid(fromDate, toDate)
        ? await fetchAuditLogsViaApi()
        : buildEmptyResultSet();

      dispatchPageMetadata({
        type: "ON_PAGE_READ_SUCCESS",
        currentPageToken: continuationToken,
        nextPageToken: resultSet.nextPageToken,
      });

      setAuditLogResultSet(resultSet);
    } catch (error) {
      showErrorMsg(error.toString());
    }
    setBusy(false);
  };

  const onSearchDateRangeUpdate = async (newfromDateUtc, newToDateUtc) => {
    setFromDateUtc(newfromDateUtc);
    setToDateUtc(newToDateUtc);
    await getAuditLogs(newfromDateUtc, newToDateUtc);
  };

  const onPagePrev = async () => {
    const prevPageToken = getPreviousPageTokenForPageRead(
      pageMetadata,
      dispatchPageMetadata
    );

    await getAuditLogs(
      fromDateUtc,
      toDateUtc,
      cachedAuditLogPageSize,
      prevPageToken
    );
  };

  const onPageNext = async () => {
    if (!pageMetadata?.nextPageToken || pageMetadata.nextPageToken.length < 1) {
      return;
    }

    await getAuditLogs(
      fromDateUtc,
      toDateUtc,
      cachedAuditLogPageSize,
      pageMetadata.nextPageToken
    );
  };

  const onSearchSubmit = async () => {
    await getAuditLogs();
  };

  const searchFilter = (
    <form
      className="user-audit-log-search-filter"
      onSubmit={(e) => {
        e.preventDefault();
        onSearchSubmit();
      }}
    >
      <div className="user-audit-log-search-filter-body">
        <UtcDateRange
          idPrefix="user-audit-log-date-range"
          fromDateUtc={fromDateUtc}
          toDateUtc={toDateUtc}
          onChange={onSearchDateRangeUpdate}
          fromLabel="From"
          toLabel="To"
          inlineLabels={true}
        />
        <SearchButton id="user-audit-log-search-btn" type="submit" />
      </div>
    </form>
  );

  const auditLogTable = (
    <div className="user-audit-log-table">
      <AuditLogTable
        auditLogResultSet={auditLogResultSet}
        loading={busy}
        onPagePrev={onPagePrev}
        onPageNext={onPageNext}
        pageNo={pageMetadata.previousPageTokens.length + 1}
        lastPage={!pageMetadata.nextPageToken}
        renderSubjectColumn={false}
      />
    </div>
  );

  return (
    <>
      {searchFilter}
      {showError ? (
        <ErrorMessage
          message={
            errorMsg && errorMsg.length > 0 ? errorMsg : "The operation failed"
          }
        />
      ) : (
        auditLogTable
      )}
    </>
  );
};

export default UserAuditLogs;
